import {
  BrowserStorageCache,
  KatalWeblabClient,
  Weblab,
} from '@amzn/katal-weblab';
import { KatalWebSellerCentralClient } from '@amzn/katal-weblab-sellercentral';

import WeblabTreatment, {
  GetWeblabTreatmentResponse,
} from 'src/model/WeblabTreatment';
import RevCalMetricsUtil from 'src/utils/RevCalMetricsUtil';

/**
 * Uses the browser's built in Session Storage to provide a cache that can survive page reloads and navigations.
 * This cache will be cleared once the browser tab or window is closed.
 */
const cacheTTLSeconds = 10 * 60; // 10 minutes * 60 seconds

/**
 * This information is required from each weblab when using the SellerCentralWeblabClient to call the Mons Proxy.
 * The proxy works by reading the Mons Context for you and setting the CustomerId, MerchantId, and Session.
 */
enum WeblabType {
  CUSTOMER = 'customer',
  MERCHANT = 'merchant',
  SESSION = 'session',
}

export enum WeblabName {
  REV_CAL_IMPORT_FEES_802564 = 'REV_CAL_IMPORT_FEES_802564',
  RC_IRC_PROVIDED_COST_851922 = 'RC_IRC_PROVIDED_COST_851922',
  SELLER_SERVICES_964586 = 'SELLER_SERVICES_964586',
  IPSF_CHANGE_RC_1105411 = 'IPSF_CHANGE_RC_1105411',
}

const allowListedWebLabs = new Set<string>(['SELLER_SERVICES_964586']);
export const weblabClient = new KatalWeblabClient({
  metricsPublisher: RevCalMetricsUtil.getMetricsPublisher(),
  weblabProxy: new KatalWebSellerCentralClient(),
  treatmentCache: new BrowserStorageCache('RevCal', cacheTTLSeconds),
});

export function getWeblabList(): Weblab[] {
  return [
    {
      defaultTreatment: 'C',
      id: WeblabName.REV_CAL_IMPORT_FEES_802564,
      weblabType: WeblabType.CUSTOMER,
    },
    {
      defaultTreatment: 'C',
      id: WeblabName.RC_IRC_PROVIDED_COST_851922,
      weblabType: WeblabType.CUSTOMER,
    },
    {
      // Weblab to enable Mega Menu integration with Revenue calculator
      defaultTreatment: 'C',
      id: WeblabName.SELLER_SERVICES_964586,
      weblabType: WeblabType.SESSION,
      marketplaceId: 'ATVPDKIKX0DER',
    },
    {
      defaultTreatment: 'C',
      id: WeblabName.IPSF_CHANGE_RC_1105411,
      weblabType: WeblabType.CUSTOMER,
    },
  ];
}

export function callWeblabTreatments() {
  const weblabList = getWeblabList();

  return weblabClient.getTreatments(weblabList).then((treatments) => {
    const weblabTreatments: WeblabTreatment[] = [];

    weblabList.forEach((weblab) => {
      if (treatments[weblab.id]?.treatmentValue) {
        weblabTreatments.push({
          weblabId: weblab.id,
          treatment: treatments[weblab.id].treatmentValue!,
        });
        if (
          weblab.weblabType === WeblabType.MERCHANT ||
          weblab.weblabType === WeblabType.CUSTOMER ||
          allowListedWebLabs.has(weblab.id)
        ) {
          treatments[weblab.id].recordTrigger();
        }
      } else {
        weblabTreatments.push({ weblabId: weblab.id, treatment: 'C' });
      }
    });

    return weblabTreatments;
  });
}

export function getWeblabTreatment(
  weblabTreatmentsRes: GetWeblabTreatmentResponse,
  weblabName: WeblabName,
): string {
  const weblab = weblabTreatmentsRes.weblabTreatments.find(
    (weblab) => weblab.weblabId === weblabName,
  );
  return weblab ? weblab.treatment : 'C';
}
